// PageGuide — a small "eye" button that opens a popup explaining the page.
//
// Drop it into any page header:  <PageGuide id="tools" />
// The dashboard uses <PageGuide id="dashboard" autoOpenOnce /> so the
// audience overview greets first-time visitors once, then stays one click away.
//
// Content lives in ./pageGuides.js — this file is just the presentation.

import React, { useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  HStack,
  Icon,
  IconButton,
  List,
  ListIcon,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { FiArrowRight, FiEye } from "react-icons/fi";
import { PAGE_GUIDES } from "./pageGuides";

const seenKey = (id) => `pageGuideSeen:${id}`;

const hasSeen = (id) => {
  try {
    return localStorage.getItem(seenKey(id)) === "true";
  } catch {
    return false;
  }
};

const markSeen = (id) => {
  try {
    localStorage.setItem(seenKey(id), "true");
  } catch {
    /* storage unavailable — guide just won't auto-open next time */
  }
};

// A single "useful function" row inside an audience group. Clickable when it
// carries a route, so "where to look" actually takes the user there.
function AudienceItem({ item, onNavigate }) {
  const body = (
    <Box>
      <Text fontWeight="semibold" fontSize="sm" color="gray.800">
        {item.title}
        {item.to ? (
          <Icon
            as={FiArrowRight}
            boxSize={3}
            ml={1.5}
            mb="2px"
            color="gwbi.500"
          />
        ) : null}
      </Text>
      {item.detail ? (
        <Text fontSize="xs" color="text.muted" mt={0.5}>
          {item.detail}
        </Text>
      ) : null}
    </Box>
  );

  if (!item.to) {
    return (
      <Box px={2} py={1.5}>
        {body}
      </Box>
    );
  }

  return (
    <Box
      as={RouterLink}
      to={item.to}
      onClick={onNavigate}
      display="block"
      px={2}
      py={1.5}
      borderRadius="md"
      transition="background 0.15s ease"
      _hover={{ bg: "surface.subtle", textDecoration: "none" }}
    >
      {body}
    </Box>
  );
}

export default function PageGuide({
  id,
  autoOpenOnce = false,
  size = "sm",
  iconButtonProps = {},
  label,
}) {
  const guide = PAGE_GUIDES[id];
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Greet first-time visitors, then never auto-open again for this guide.
  useEffect(() => {
    if (!guide || !autoOpenOnce) return;
    if (!hasSeen(id)) {
      onOpen();
      markSeen(id);
    }
  }, [guide, autoOpenOnce, id, onOpen]);

  if (!guide) return null;

  const triggerLabel = label || `What's on this page? — ${guide.title}`;

  return (
    <>
      <Tooltip label={triggerLabel} placement="bottom" hasArrow openDelay={300}>
        <IconButton
          aria-label={triggerLabel}
          icon={<FiEye />}
          size={size}
          variant="ghost"
          color="text.muted"
          borderRadius="full"
          _hover={{ bg: "surface.subtle", color: "gwbi.600" }}
          onClick={onOpen}
          {...iconButtonProps}
        />
      </Tooltip>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.500" />
        <ModalContent borderRadius="ui-lg" mx={4}>
          <ModalHeader pb={2}>
            <HStack spacing={3} align="center">
              <Box
                bg="gwbi.50"
                color="gwbi.600"
                borderRadius="full"
                w="34px"
                h="34px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <Icon as={FiEye} boxSize={4} />
              </Box>
              <Text
                fontFamily="heading"
                fontWeight="bold"
                fontSize="xl"
                color="gray.900"
              >
                {guide.title}
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {guide.intro ? (
              <Text color="gray.700" fontSize="sm" lineHeight="1.6">
                {guide.intro}
              </Text>
            ) : null}

            {(guide.sections || []).map((section) => {
              // A section is either a list of plain bullets (strings) or a list
              // of rich, linkable rows ({ title, detail, to }) — e.g. the tools
              // shown on a page. Detect which and render accordingly.
              const isLinkList = section.items.some(
                (it) => it && typeof it === "object" && it.title,
              );
              return (
                <Box key={section.heading} mt={5}>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    letterSpacing="0.12em"
                    textTransform="uppercase"
                    color="text.muted"
                    mb={2}
                  >
                    {section.heading}
                  </Text>
                  {isLinkList ? (
                    <VStack align="stretch" spacing={0.5}>
                      {section.items.map((entry, idx) => (
                        <AudienceItem
                          key={idx}
                          item={
                            typeof entry === "string"
                              ? { title: entry }
                              : entry
                          }
                          onNavigate={onClose}
                        />
                      ))}
                    </VStack>
                  ) : (
                    <List spacing={2}>
                      {section.items.map((entry, idx) => {
                        const text =
                          typeof entry === "string" ? entry : entry.text;
                        return (
                          <ListItem
                            key={idx}
                            display="flex"
                            alignItems="flex-start"
                            fontSize="sm"
                            color="gray.700"
                          >
                            <ListIcon
                              as={FiArrowRight}
                              color="gwbi.500"
                              mt={1}
                              flexShrink={0}
                            />
                            <Text>{text}</Text>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Box>
              );
            })}

            {guide.audiences ? (
              <Box mt={6}>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  letterSpacing="0.12em"
                  textTransform="uppercase"
                  color="text.muted"
                  mb={3}
                >
                  {guide.audiencesHeading || "What's here for you"}
                </Text>
                <Accordion allowToggle defaultIndex={[0]} reduceMotion>
                  <VStack align="stretch" spacing={2}>
                    {guide.audiences.map((aud) => (
                      <AccordionItem
                        key={aud.key}
                        border="1px solid"
                        borderColor="border.default"
                        borderRadius="ui-md"
                        overflow="hidden"
                        bg="surface.card"
                      >
                        <AccordionButton
                          px={3}
                          py={3}
                          _hover={{ bg: "surface.subtle" }}
                        >
                          <HStack spacing={3} flex="1" textAlign="left">
                            <Box
                              bg="gwbi.50"
                              color="gwbi.600"
                              borderRadius="full"
                              w="30px"
                              h="30px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              flexShrink={0}
                            >
                              <Icon as={aud.icon} boxSize={3.5} />
                            </Box>
                            <Box>
                              <Text
                                fontWeight="semibold"
                                fontSize="sm"
                                color="gray.800"
                              >
                                {aud.label}
                              </Text>
                              {aud.blurb ? (
                                <Text fontSize="xs" color="text.muted">
                                  {aud.blurb}
                                </Text>
                              ) : null}
                            </Box>
                          </HStack>
                          <AccordionIcon color="text.muted" />
                        </AccordionButton>
                        <AccordionPanel px={2} pb={3} pt={0}>
                          <VStack align="stretch" spacing={0.5}>
                            {aud.items.map((item, idx) => (
                              <AudienceItem
                                key={idx}
                                item={item}
                                onNavigate={onClose}
                              />
                            ))}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </VStack>
                </Accordion>
              </Box>
            ) : null}

            <Text fontSize="xs" color="text.muted" mt={6}>
              Tip: click the eye icon anytime to reopen this guide.
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
